import { useRef, useState } from 'react';
import { date, money, todayYmd } from '../lib/format';
import { errorMessage } from '../lib/api-error';
import { toast } from '../lib/toast';
import { saveBlob } from '../lib/einvoice-api';
import { usePostDocument, useSourceEntry } from '../lib/accounting-api';
import { useAuth } from '../lib/auth-context';
import { hasArea, isReadOnly } from '../lib/permissions';
import {
  downloadArchivedXml,
  downloadReceipt,
  useDiscardVatDeclaration,
  useRecordVatDeclaration,
  useRecordVatPayment,
  useRemoveVatPayment,
  useUploadVatReceipt,
  useVatDeclarations,
  useVatPayments,
  VAT_DECLARATION_STATUS_LABELS,
  VAT_DECLARATION_TYPE_LABELS,
  type VatDeclarationRecord,
  type VatDeclarationType,
} from '../lib/vat-return-api';
import { Badge, Button, Card, CardHeader, EmptyState, Input, Spinner } from '../components/ui';

/**
 * BEYAN KAYITLARI — hazırlanan dosya, arşivi ve makbuzu.
 *
 * ⚠⚠ BU EKRAN BEYAN GÖNDERMEZ ve göndermediğini SÖYLER. Yazılımın Intervat'a
 * bağlantısı yok; "hazırla" yalnız dosyayı dondurup arşivler. Beyan, operatör
 * dosyayı portala yükleyip imzalı makbuzu buraya geri getirdiğinde verilmiş
 * sayılır — çünkü o makbuz, verildiğinin TEK hukuki kanıtıdır.
 *
 * ⚠ "HAZIRLANDI" YEŞİL DEĞİL. Süresi dolmakta olan bir beyanın hazırlanmış
 * olması, verilmiş olması demek değildir; iki durumu aynı renkte göstermek
 * operatörü tam da kaçırmaması gereken şeyde yanıltır.
 */
export function VatDeclarationRecords(props: { period: string; year: number; nihil: boolean }) {
  const kayitlar = useVatDeclarations();
  const hazirla = useRecordVatDeclaration();
  const sil = useDiscardVatDeclaration();
  const [hata, setHata] = useState<string | null>(null);

  function calistir(type: VatDeclarationType, periodKey: string) {
    setHata(null);
    hazirla.mutate(
      { type, periodKey, clientListingNihil: type === 'PERIODIC' ? props.nihil : undefined },
      { onError: (e: unknown) => setHata(errorMessage(e)) },
    );
  }

  const satirlar = kayitlar.data ?? [];

  return (
    <Card className="mt-4">
      <CardHeader
        title="Beyan kayıtları"
        description="Dondurulan dosya, arşivi ve imzalı makbuzu. Kayıt açmak beyanı GÖNDERMEZ."
      />

      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--c-border)] px-4 py-3">
        <Button
          variant="secondary"
          disabled={hazirla.isPending}
          onClick={() => calistir('PERIODIC', props.period)}
        >
          {props.period} periyodik beyanı hazırla
        </Button>
        <Button
          variant="secondary"
          disabled={hazirla.isPending}
          onClick={() => calistir('IC_LISTING', props.period)}
        >
          {props.period} · 723 hazırla
        </Button>
        {/*
          ⚠ 725 YILLIKTIR: dönem anahtarı yalnız YIL taşır. Buraya `2026Q4`
          göndermek dosyayı geçersiz kılardı.
        */}
        <Button
          variant="secondary"
          disabled={hazirla.isPending}
          onClick={() => calistir('CLIENT_LISTING', String(props.year))}
        >
          {props.year} · 725 hazırla
        </Button>
        {hazirla.isPending && <Spinner />}
      </div>

      {hata && (
        <p className="border-b border-[var(--c-border)] px-4 py-2 text-xs text-[var(--c-danger)]">
          {hata}
        </p>
      )}

      {kayitlar.isPending ? (
        <div className="px-4 py-6">
          <Spinner />
        </div>
      ) : satirlar.length === 0 ? (
        <EmptyState
          title="Henüz beyan kaydı yok"
          description="Bir dönem hazırladığınızda dosya dondurulur ve arşivlenir."
        />
      ) : (
        <div className="divide-y divide-[var(--c-border)]">
          {satirlar.map((k) => (
            <DeclarationRow
              key={k.id}
              kayit={k}
              onCorrect={(reference) => {
                setHata(null);
                hazirla.mutate(
                  {
                    type: 'PERIODIC',
                    periodKey: k.periodKey,
                    clientListingNihil: props.nihil,
                    replacesDeclarationId: k.id,
                    ...(reference ? { replacedDeclarationReference: reference } : {}),
                  },
                  { onError: (e: unknown) => setHata(errorMessage(e)) },
                );
              }}
              correcting={hazirla.isPending}
              onDiscard={() => {
                setHata(null);
                sil.mutate(k.id, { onError: (e: unknown) => setHata(errorMessage(e)) });
              }}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function DeclarationRow(props: {
  kayit: VatDeclarationRecord;
  onDiscard: () => void;
  onCorrect: (reference: string | null) => void;
  correcting: boolean;
}) {
  const { kayit } = props;
  const yukle = useUploadVatReceipt();
  const dosyaGirisi = useRef<HTMLInputElement>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  /*
   * ⚠ INTERVAT REFERANSI MAKBUZLA BİRLİKTE SORULUR. Düzeltici beyan
   * (`<ReplacedVATDeclaration>`) bu referansı ister ve tek kaynağı makbuzdur;
   * yükleme anında girilmezse düzeltme günü — çoğu zaman son gün — aranır.
   */
  const [makbuzRef, setMakbuzRef] = useState('');
  const [duzeltRef, setDuzeltRef] = useState('');

  const gonderildi = kayit.status === 'SUBMITTED';
  const degistirildi = kayit.status === 'REPLACED';
  const duzeltici = kayit.replacesDeclarationId !== null;
  /*
   * ⚠ SÜRE KONTROLÜNÜ SUNUCU YAPAR (Brüksel günüyle); buradaki yalnız düğmeyi
   * gizlemek içindir — süresi geçmiş beyanın düzeltmesi kutu 61/62'dir.
   */
  const duzeltilebilir =
    gonderildi && kayit.declarationType === 'PERIODIC' && todayYmd() <= kayit.dueDate.slice(0, 10);

  return (
    <div className="space-y-2 px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">
          {VAT_DECLARATION_TYPE_LABELS[kayit.declarationType]} · {kayit.periodKey}
        </span>
        {/*
          ⚠ RENK BİR İDDİADIR. `PREPARED` uyarı rengindedir çünkü beyan HENÜZ
          VERİLMEMİŞTİR; nötr göstermek "iş bitti" izlenimi verirdi.
        */}
        <Badge tone={gonderildi ? 'ok' : degistirildi ? 'neutral' : 'warn'}>
          {VAT_DECLARATION_STATUS_LABELS[kayit.status]}
        </Badge>
        {duzeltici && <Badge tone="neutral">düzeltici beyan</Badge>}
      </div>

      <p className="text-xs text-[var(--c-ink-soft)]">
        {date(kayit.periodStart)} – {date(kayit.periodEnd)} · son gün{' '}
        <strong>{date(kayit.dueDate)}</strong>
        {kayit.totalDue !== null && (
          <>
            {' '}
            · hesaplanan KDV {money(kayit.totalDue)} · indirilecek{' '}
            {money(kayit.totalDeductible ?? '0.00')}
            {kayit.payableAmount !== null && (
              <>
                {' '}
                · <strong>ödenecek {money(kayit.payableAmount)}</strong>
              </>
            )}
          </>
        )}
      </p>

      {/*
        ⚠ ÖZET GÖSTERİLİYOR ve bu süs değil: arşivden indirilen dosyanın bu
        özetle tuttuğu her okumada doğrulanıyor. Operatör, portala yüklediği
        dosyanın kayıttakiyle aynı olduğunu buradan görebilir.
      */}
      <p className="font-mono text-[11px] text-[var(--c-ink-soft)]">
        {kayit.fileName} · {kayit.xmlByteLength} bayt · sha256 {kayit.xmlSha256.slice(0, 16)}…
      </p>

      {gonderildi && (
        <p className="text-xs text-[var(--c-ink-soft)]">
          Gönderim: {date(kayit.submittedAt ?? '')}
          {kayit.submissionRef ? ` · referans ${kayit.submissionRef}` : ''}
        </p>
      )}

      {/*
        ⚠⚠ BEYANI GÖNDERMEK KDV BORCUNU SIFIRLAMAZ. Yalnız periyodik ve
        gönderilmiş beyanda anlamlı — 723/725'in ödenecek KDV'si yok.
      */}
      {gonderildi && kayit.declarationType === 'PERIODIC' && kayit.totalDue !== null && (
        <VatPaymentSection kayit={kayit} />
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          disabled={mesgul}
          onClick={() => {
            setHata(null);
            setMesgul(true);
            downloadArchivedXml(kayit.id, kayit.fileName)
              .then((f) => saveBlob(f.blob, f.fileName))
              .catch((e: unknown) => setHata(errorMessage(e)))
              .finally(() => setMesgul(false));
          }}
        >
          Arşivdeki XML
        </Button>

        {!gonderildi && !degistirildi && (
          <>
            {/*
              ⚠ MAKBUZ YÜKLEMEK "GÖNDERİLDİ" DEMENİN TEK YOLU. Ayrı bir
              "gönderildi işaretle" düğmesi bilerek YOK: kanıtsız bir gönderim
              kaydı, yazılımın operatör adına iddiada bulunması olurdu.
            */}
            <input
              ref={dosyaGirisi}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                setHata(null);
                yukle.mutate(
                  {
                    id: kayit.id,
                    file,
                    ...(makbuzRef.trim() ? { submissionRef: makbuzRef.trim() } : {}),
                  },
                  { onError: (err: unknown) => setHata(errorMessage(err)) },
                );
              }}
            />
            {kayit.declarationType === 'PERIODIC' && (
              <Input
                label="Intervat referansı (makbuzda)"
                hint="ör. 12345-1234567890-123456 — sonradan düzeltici beyan için gerekir"
                value={makbuzRef}
                onChange={(e) => setMakbuzRef(e.target.value)}
              />
            )}
            <Button disabled={yukle.isPending} onClick={() => dosyaGirisi.current?.click()}>
              İmzalı makbuzu yükle
            </Button>
            <Button variant="ghost" disabled={mesgul} onClick={props.onDiscard}>
              Kaydı sil
            </Button>
          </>
        )}

        {gonderildi && (
          <Button
            variant="secondary"
            disabled={mesgul}
            onClick={() => {
              setHata(null);
              setMesgul(true);
              downloadReceipt(kayit.id)
                .then((f) => saveBlob(f.blob, f.fileName))
                .catch((e: unknown) => setHata(errorMessage(e)))
                .finally(() => setMesgul(false));
            }}
          >
            Makbuzu indir
          </Button>
        )}

        {duzeltilebilir && (
          <>
            {!kayit.submissionRef && (
              <Input
                label="Bu beyanın Intervat referansı"
                hint="Makbuzda yazar. Düzeltici beyan bu referansla eşleşir."
                value={duzeltRef}
                onChange={(e) => setDuzeltRef(e.target.value)}
              />
            )}
            <Button
              variant="secondary"
              disabled={props.correcting}
              onClick={() => props.onCorrect(kayit.submissionRef ? null : duzeltRef.trim() || null)}
            >
              Düzeltici beyan hazırla
            </Button>
          </>
        )}
      </div>

      {duzeltilebilir && (
        <p className="text-xs text-[var(--c-ink-soft)]">
          Süre dolmadan önce bu dönemin beyanı düzeltilebilir. Yeni dosya bugünkü kayıtlarla
          hazırlanır; bu beyan, düzeltmenin makbuzu yüklendiğinde &ldquo;değiştirildi&rdquo; olur.
        </p>
      )}

      {!gonderildi && !degistirildi && (
        <p className="text-xs text-[var(--c-ink-soft)]">
          Dosyayı Intervat portalına yükleyin, sonra portalın verdiği{' '}
          <strong>imzalı PDF makbuzu</strong> buraya geri yükleyin. Beyan ancak o zaman verilmiş
          sayılır — ürettiğimiz XML bir kanıt değildir, onu biz yazdık.
        </p>
      )}

      {hata && <p className="text-xs text-[var(--c-danger)]">{hata}</p>}
    </div>
  );
}

/**
 * KDV'NİN DEVLETE FİİLEN ÖDENMESİ — beyanın gönderilmesinden AYRI bir olgu.
 *
 * ⚠⚠ "GÖNDERİLDİ" BADGE'İNİN YANINA KOYULMASI BİLİNÇLİ: operatör beyanı
 * gönderdiğinde işinin bittiğini düşünebilir — oysa borç ancak devlete
 * gerçekten para gönderilince kapanır. `remainingAmount` sunucudan gelir,
 * panel hiçbir tutarı kendisi çıkarmaz.
 */
function VatPaymentSection({ kayit }: { kayit: VatDeclarationRecord }) {
  const { user } = useAuth();
  const payments = useVatPayments(kayit.id);
  const record = useRecordVatPayment();
  const remove = useRemoveVatPayment();
  const post = usePostDocument();
  // Defter işi `accounting` alanınındır; o alanı olmayana düğme gösterilmez.
  const canPost = hasArea(user, 'accounting') && !isReadOnly(user);
  const settlement = useSourceEntry('VAT_SETTLEMENT', kayit.id, canPost);

  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [paidOn, setPaidOn] = useState(todayYmd());
  const [reference, setReference] = useState('');
  const [hata, setHata] = useState<string | null>(null);

  const kalanSıfırDeğil = kayit.remainingAmount !== null && kayit.remainingAmount !== '0.00';
  const fazla = kayit.overpaidAmount !== null && kayit.overpaidAmount !== '0.00';

  return (
    <div className="rounded-[var(--r-md)] border border-[var(--c-border)] bg-[var(--c-surface-alt)] px-3 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold text-[var(--c-ink-soft)]">KDV'nin ödenmesi</p>
          <p className="text-sm">
            {/*
              ⚠ PAYDA KUTU 71'DİR (`payableAmount`), brüt `totalDue` DEĞİL. Bir
              süre brüt tutar gösteriliyordu ve indirimli ilk beyanda (2026Q3)
              tam ödenmiş dönem "kalan 56,97" diye görünüyordu.
            */}
            {money(kayit.paidAmount)} / {money(kayit.payableAmount ?? kayit.totalDue)}
            {kalanSıfırDeğil && (
              <span className="text-[var(--c-ink-soft)]">
                {' '}
                · kalan {money(kayit.remainingAmount)}
              </span>
            )}
            {fazla && (
              <span className="text-[var(--c-ink-soft)]">
                {' '}
                · fazla ödeme {money(kayit.overpaidAmount)}
              </span>
            )}
          </p>
        </div>
        <Button
          variant={kalanSıfırDeğil ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => {
            setHata(null);
            setAmount(kalanSıfırDeğil ? (kayit.remainingAmount ?? '') : '');
            setOpen((v) => !v);
          }}
        >
          {open ? 'Vazgeç' : 'Ödeme ekle'}
        </Button>
      </div>

      {open && (
        <div className="mt-3 space-y-2">
          <Input
            label="Tutar"
            hint="Nokta ile: 221.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <Input
            label="Ödeme günü"
            type="date"
            hint="Bankadan devlete PARANIN ÇIKTIĞI gün — kaydı girdiğiniz gün değil."
            value={paidOn}
            onChange={(e) => setPaidOn(e.target.value)}
          />
          <Input
            label="Banka referansı (isteğe bağlı)"
            hint="OGM / yapılandırılmış bildirim varsa"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
          <Button
            disabled={record.isPending}
            onClick={() => {
              setHata(null);
              record
                .mutateAsync({
                  declarationId: kayit.id,
                  amount,
                  paidOn,
                  reference: reference.trim() === '' ? undefined : reference,
                })
                .then(() => {
                  setOpen(false);
                  toast.success('Ödeme kaydedildi.');
                })
                .catch((e: unknown) => setHata(errorMessage(e)));
            }}
          >
            Kaydet
          </Button>
          {hata !== null && <p className="text-xs text-[var(--c-danger)]">{hata}</p>}
        </div>
      )}

      {canPost && settlement.isSuccess && (
        <div className="mt-2 flex items-center gap-2 text-xs">
          <span className="text-[var(--c-ink-soft)]">KDV mahsubu:</span>
          {settlement.data ? (
            <Badge tone="ok">defterde · {settlement.data.entryNumber}</Badge>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              disabled={post.isPending}
              onClick={() => {
                setHata(null);
                post
                  .mutateAsync({ sourceType: 'VAT_SETTLEMENT', sourceId: kayit.id })
                  .then(() => settlement.refetch())
                  .catch((e: unknown) => setHata(errorMessage(e)));
              }}
            >
              Mahsubu deftere kaydet
            </Button>
          )}
        </div>
      )}

      {payments.isSuccess && payments.data.length > 0 && (
        <ul className="mt-3 space-y-1 text-xs">
          {payments.data.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-2">
              <span>
                {date(p.paidOn)} · {money(p.amount)}
                {p.reference !== null && (
                  <span className="text-[var(--c-ink-faint)]"> · {p.reference}</span>
                )}
              </span>
              <span className="flex items-center gap-1">
                {p.journalEntryNumber ? (
                  <Badge tone="ok">defterde · {p.journalEntryNumber}</Badge>
                ) : (
                  canPost && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={post.isPending}
                      onClick={() => {
                        setHata(null);
                        post
                          .mutateAsync({ sourceType: 'VAT_PAYMENT', sourceId: p.id })
                          .then(() => payments.refetch())
                          .catch((e: unknown) => setHata(errorMessage(e)));
                      }}
                    >
                      Deftere kaydet
                    </Button>
                  )
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={remove.isPending}
                  onClick={() => {
                    setHata(null);
                    remove
                      .mutateAsync({ declarationId: kayit.id, paymentId: p.id })
                      .catch((e: unknown) => setHata(errorMessage(e)));
                  }}
                >
                  Sil
                </Button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
